import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import Header from './Header';
import Footer from './Footer';
import ApiAccessSection from './ApiAccessSection';
import ApiRequestPlayground from './ApiRequestPlayground';
import { useAuth } from '../contexts/AuthContext';
import { API_PUBLIC_ROOT } from '../services/api';
import './DeveloperApiPage.css';

const STEPS = [
  { title: 'Create an account', body: 'Sign up with Google, or with an email address and password.' },
  { title: 'Verify your email', body: 'Click the link we send you. Unverified accounts cannot hold a key.' },
  { title: 'Create your key', body: 'One key per account. It is shown once  copy it somewhere safe.' },
  { title: 'Call the API', body: 'Send your key as an X-API-Key header from your server.' },
];

/**
 * Public entry point for developer API access.
 *
 * Signed-in users get the full key-management panel; signed-out visitors get
 * the pitch and a sign-up CTA, so the header and footer links never dead-end
 * the way a direct /profile link would.
 */
export default function DeveloperApiPage() {
  const { user, openAuthModal } = useAuth();

  return (
    <div className="dev-api-page">
      <SEO
        title="Developer API"
        description="Track containers and parcels from your own systems with the RideLink tracking API. Create a free API key in minutes."
        canonicalPath="/developers"
      />
      <Header />

      <main className="dev-api-main">
        <div className="dev-api-container">

          <header className="dev-api-hero">
            <p className="dev-api-eyebrow">Developer API</p>
            {/* <h1 className="dev-api-title">Tracking data, straight into your systems</h1> */}
            <p className="dev-api-lede">
              One endpoint for containers, bills of lading and booking references, aggregated
              across every carrier we support. Authenticate with an API key from your server 
              no SDK, no OAuth dance.
            </p>
            <div className="dev-api-hero-actions">
              {!user && (
                <button className="dev-api-cta" onClick={openAuthModal}>
                  Create a free account
                </button>
              )}
              <a
                className="dev-api-cta ghost"
                href={`${API_PUBLIC_ROOT}/api-docs`}
                target="_blank"
                rel="noreferrer"
              >
                Read the API docs ↗
              </a>
            </div>
          </header>

          <section className="dev-api-example-block">
            <span className="dev-api-block-label">One request</span>
            <pre className="dev-api-code">
              <code>{`curl -X POST ${API_PUBLIC_ROOT}/v1/track \\
  -H "X-API-Key: rl_live_YOUR_KEY_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{"trackingId": "MSCU1234567"}'`}</code>
            </pre>
          </section>

          {user ? (
            <>
              <ApiAccessSection />
              <ApiRequestPlayground />
            </>
          ) : (
            <section className="dev-api-steps-block">
              <h2 className="dev-api-section-title">Getting started</h2>
              <ol className="dev-api-steps">
                {STEPS.map((step, index) => (
                  <li key={step.title}>
                    <span className="dev-api-step-num">{index + 1}</span>
                    <div>
                      <p className="dev-api-step-title">{step.title}</p>
                      <p className="dev-api-step-body">{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <button className="dev-api-cta" onClick={openAuthModal}>
                Get your API key
              </button>
            </section>
          )}

          <section className="dev-api-limits-block">
            <h2 className="dev-api-section-title">Limits</h2>
            <p className="dev-api-limits-body">
              Every key allows <strong>10 requests per minute</strong> and{' '}
              <strong>1,000 requests per month</strong>. Responses carry your remaining allowance,
              and going over returns <code>429</code> with a <code>Retry-After</code> header.
              Need more? <Link to="/faq">Get in touch</Link> — we raise limits per account.
            </p>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
}
